<script lang="ts">
    import { Component, mixins, Prop, Watch } from "vue-facing-decorator";
    import {
        AnalyticsCustomEvent,
        ApiService,
        CustomEventId,
        DomEvent,
        GAEcommerceEvent,
        GAEventAction,
        GAEventCategory,
        GAUtil,
        GTMUtils,
        IconName,
        StorageService,
        TranslationKey,
    } from "webshop-modules";
    import { MainMenuSection, MenuItemAction } from "@/domain/main-header/entity/MainMenu.enum";
    import IconComponent from "@/components/atoms/Icon.vue";
    import { capitalize } from "vue";
    import { CartStatus } from "@/domain/main-header/entity/CartStatus.interface";
    import { UserStatus } from "@/domain/main-header/entity/UserStatus.interface";
    import { MenuTitle } from "@/domain/main-header/entity/MenuTitle.interface";
    import { MenuTitleSubCategory } from "@/domain/main-header/entity/MenuTitleSubCategory.interface";
    import { MenuTitleCategory } from "@/domain/main-header/entity/MenuTtitleCategory.interface";
    import { Profile } from "@/domain/main-header/entity/Profile.interface";
    import { ProfileCategory } from "@/domain/main-header/entity/ProfileCategory.interface";
    import { IMainHeader } from "@/domain/main-header/entity/MainHeader.interface";
    import { LanguageConfiguration } from "@/domain/main-header/entity/LanguageConfiguration.interface";
    import { BreakpointHandler } from "@/mixins";

    @Component({
    components: {
        IconComponent
    }
})
export default class MenuComponent extends mixins(BreakpointHandler) {
    @Prop() public data!: IMainHeader;
    @Prop() public languageConfiguration!: LanguageConfiguration;
    @Prop() public cartStatus: CartStatus | null = null;
    @Prop() public userStatus: UserStatus | null = null;
    @Prop() public activeSection: MenuTitle | Profile | null = null;

    public deferredPrompt: any = null;
    public EventPromptA2HS: any;
    public loggedIn = false;
    public showA2HStrigger = false;
    public hasPromotions = false;
    public filteredActiveSection: Profile | MenuTitle | null = null;

    public readonly GAUtil: typeof GAUtil = GAUtil;
    public readonly IconName: typeof IconName = IconName;
    public readonly MainMenuSection: typeof MainMenuSection = MainMenuSection;
    public readonly Translation: typeof TranslationKey = TranslationKey;

    @Watch('activeSection')
    handleActiveSectionChange(newVal: MenuTitle | null, oldVal: MenuTitle | null): void {
        this.filteredActiveSection = this.activeSectionFiltered(newVal);
    }

    public actionMenuItemClick(item: MenuTitle | Profile | ProfileCategory | MenuTitleCategory | MenuTitleSubCategory): void {
        let clickText: string = item.title;

        if (this.isMenuItem(item)) {
            clickText = item.name;
        }

        if (this.activeSection && this.activeSection.name) {
            this.handleGACategoryClick(clickText, this.activeSection.name);
        }
    }

    public handleGACategoryClick(clickText: string, mainNavigationText: string): void {
        const eventData: AnalyticsCustomEvent = {
            event: 'sub_navigation_click',
            click_text: capitalize(clickText),
            main_navigation_text: mainNavigationText,
        };

        GTMUtils.pushAnalyticsEventToDataLayer(eventData);
    }

    public isActiveSection(name: MainMenuSection): boolean {
        const section = this.filteredActiveSection;

        if (section && section.name === name) {
            return true;
        }

        return false;
    }

    public categoryClick(category: ProfileCategory | MenuTitleCategory, event: Event) {
        if (this.isProfileType(this.activeSection)) {
            this.handleProfileCategoryClick(this.activeSection, category.name, event);
        }

        if (this.activeSection && this.activeSection.name) {
            this.handleGACategoryClick(category.title, this.activeSection.name);
        }
    }

    public handeCategoryClick(item: ProfileCategory | MenuTitleCategory, event: Event): void {
        if (!this.activeSection || !this.activeSection.categories) {
            return;
        }

        if (this.isProfileType(this.activeSection)) {
            this.handleProfileCategoryClick(this.activeSection, item.name, event);
        }

        if (this.isMenuTitleType(this.activeSection)) {
            this.handleMenuTitleCategoryClick(this.activeSection, item, event);
        }
    }

    public toggleContent(item: string, event: Event): void {
        this.$emit('toggleContent', item, event);
    }

    public isHidden(category: ProfileCategory | MenuTitleCategory): boolean {
        if (!this.isProfileType(this.activeSection)) {
            return false;
        }

        const typedCategory = category as ProfileCategory;

        if (typedCategory.action && typedCategory.action === MenuItemAction.A2HS && !this.showA2HStrigger) {
            return true;
        }

        return false;
    }

    public canDisplayCartStatus(category: ProfileCategory) {
        if (!this.isProfileType(this.activeSection)) {
            return false;
        }

        return category.showCartStatus && this.cartStatus;
    }

    public actionGA(type: string, name: string, position: number): void {
        ++position;
        GTMUtils.pushEcommerceToDataLayer({
                promoClick: {
                    promotions: [
                        {
                            id: `dropdown-promotion--${type}`,
                            name: name,
                            creative: type,
                            position: `${position}`
                        }
                    ]
                }
            },
            GAEcommerceEvent.PromotionClick
        );
        GTMUtils.pushCustomEventToDataLayer({
            category: GAEventCategory.InternalPromotions,
            action: GAEventAction.Click,
            label: name
        });
    }

    public async handleLogout(): Promise<void> {
        const apiService: ApiService = this.$apiService;
        const storageService: StorageService = this.$storageService;
        if (!apiService || !storageService) {
            return;
        }

        this.handleGACategoryClick(CustomEventId.Logout, MainMenuSection.Profile);
        storageService.deletePersistence();
        storageService.deleteSessionPersistence();

        document.dispatchEvent(new Event(CustomEventId.Logout));
        await apiService.logout();

        window.location.reload();
    }

    /**
     * Mounted lifecycle hook
     */
    protected mounted() {
        this.filteredActiveSection = this.activeSectionFiltered(this.activeSection);
        this.hasPromotions = this.setupHasPromotions();
        this.loggedIn = this.isLoggedIn();
        this.showA2HStrigger = this.shouldShowA2HStrigger();

        window.addEventListener(DomEvent.DeferredPromptUpdate, (event: any) => {
            this.deferredPrompt = event.detail;
        });

        if (typeof (Event) === 'function') {
            this.EventPromptA2HS = new Event(DomEvent.PromptA2HS);
        } else {
            this.EventPromptA2HS = document.createEvent('Event');
            this.EventPromptA2HS.initEvent(DomEvent.PromptA2HS, true, true);
        }
    }

    private activeSectionFiltered(activeSection: MenuTitle | Profile | null): MenuTitle | null {
        if (!activeSection || !activeSection.categories) {
            return null;
        }

        const filteredActiveSection = activeSection;

        filteredActiveSection.categories = activeSection.categories
            .map((category) => {
                if (category.subCategories) {
                    category.showChildren = false;
                }

                return category;
            })
            .filter((category) => {
                if (this.isProfileType(filteredActiveSection)) {
                    return category.auth !== !this.loggedIn || !category.action && !category.auth;
                }

                return true;
            });

        return filteredActiveSection;
    }

    private shouldShowA2HStrigger(): any {
        return this.deferredPrompt;
    }

    private isLoggedIn(): boolean {
        if (!this.userStatus) {
            return false;
        }

        return this.userStatus.authenticated;
    }

    private setupHasPromotions(): boolean {
        if (!this.filteredActiveSection) {
            return false;
        }

        const activeSection = this.filteredActiveSection;

        return !!(this.isMenuTitleType(activeSection) && activeSection.promotions && activeSection.promotions.length > 0);
    }

    private isMenuTitleType(item: MenuTitle | Profile | null): item is MenuTitle {
        return !(item as Profile).loginUrl;
    }

    private isProfileType(item: MenuTitle | Profile | null): item is Profile {
        return !!(item as Profile).loginUrl;
    }

    private handleMenuTitleCategoryClick(menuTitle: MenuTitle, item: MenuTitleCategory, event: Event) {
        const clickedCategory = menuTitle.categories.find((category) => {
            if (item.name.length > 0) {
                return  category.name === item.name;
            }

            return item.title === category.title;
        });

        if (!clickedCategory) {
            return;
        }

        if (clickedCategory.subCategories.length > 0) {
            event.preventDefault();
            clickedCategory.showChildren = !clickedCategory.showChildren;
        } else {
            this.handleGACategoryClick(clickedCategory.title, menuTitle.name);
        }

    }

    private handleProfileCategoryClick(profile: Profile, categoryName: string, event: Event) {
        const clickedCategory = profile.categories.find((category) => category.name === categoryName);

        if (!clickedCategory) {
            return;
        }

        if (clickedCategory.action === MenuItemAction.A2HS && this.showA2HStrigger) {
            event.preventDefault();
            window.dispatchEvent(this.EventPromptA2HS);
        }
    }

    private isMenuItem(item: MenuTitle | MenuTitleCategory | MenuTitleSubCategory): item is MenuTitle {
        return (item as MenuTitle).name !== undefined;
    }
}
</script>

<template>
    <div :class="['m-mainMenu', {'has-promotions': hasPromotions}]">
        <template v-if="filteredActiveSection">
            <ul :class="[
                'm-mainMenu__list', filteredActiveSection.name,
                {'has-children': !isActiveSection(MainMenuSection.Profile)}
            ]">
                <li v-if="isActiveSection(MainMenuSection.Profile) && loggedIn" class="m-mainMenu__listItem">
                    <span class="m-mainMenu__listItemLabel"
                          v-translate="{ key: Translation.Account_section_title }">Profile:</span>
                    <strong class="m-mainMenu__listItemText is-clipped-rtl"
                            :title="userStatus?.username">{{ userStatus?.username }}</strong>
                </li>
                <li v-for="(item, index) in filteredActiveSection.categories" :key="index"
                    :class="['m-mainMenu__listItem', {'is-opened': item.showChildren, 'is-hidden': isHidden(item)}]"
                >

                    <span v-if="item.label" class="m-mainMenu__listItemLabel">
                        {{ item.label }}
                    </span>

                    <h3 :class="['m-mainMenu__listItemTitle', {'big-title': !isActiveSection(MainMenuSection.Profile)}]">
                        <a :href="item.url" :aria-label="item.title" @click="categoryClick(item, $event)">
                            <span v-if="canDisplayCartStatus(item)"
                                  class="o-mainHeader__utilitiesNavItemDot"></span>
                            <span class="m-mainMenu__listItemTitleText">{{item.title}}</span>
                        </a>
                    </h3>

                    <a :class="['m-mainMenu__listItemTitle is-mobile', {'is-opened': item.showChildren}]"
                       :href="item.url"
                       :aria-label="item.title"
                       @click="handeCategoryClick(item, $event)"
                    >
                        <div class="m-mainMenu__listItemRow">
                            <span v-if="canDisplayCartStatus(item)"
                                  class="o-mainHeader__utilitiesNavItemDot"></span>
                            <span v-html="item.title"></span>
                            <span v-if="item.subCategories && item.subCategories.length" class="a-iconArr icon-darr">
                                <IconComponent :icon-id="IconName.ArrowSlim"/>
                            </span>
                        </div>
                    </a>

                    <ul v-if="item.subCategories.length" :class="['m-mainMenu__listItemSubList', {'is-opened': item.showChildren}]">
                        <li v-if="item.url" class="m-mainMenu__listItemSubListItem is-mobile">
                            <a :href="item.url" :aria-label="item.title" @click="actionMenuItemClick(item)">
                                <span v-translate="{ key: Translation.Dropdown_all }"></span> {{ item.title }}
                            </a>
                        </li>
                        <li v-for="(subitem, idx) in item.subCategories" :key="idx" class="m-mainMenu__listItemSubListItem">
                            <a :href="subitem.url" @click="actionMenuItemClick(subitem)">{{ subitem.title }}</a>
                        </li>
                    </ul>
                </li>
                <li v-if="isActiveSection(MainMenuSection.Profile) && languageConfiguration" class="m-mainMenu__listItem">
                    <h3 class="m-mainMenu__listItemTitle">
                        <a class="m-mainMenu__listItemTitle"
                           :href="'?switch_language'"
                        >
                            <strong>{{ languageConfiguration.name }}</strong>
                        </a>
                    </h3>
                </li>
            </ul>
            <div v-if="hasPromotions" :class="['m-mainMenu__promotions', filteredActiveSection!.name]">
                <a v-for="(promo, index) in (filteredActiveSection as MenuTitle).promotions" :key="index"
                   class="m-mainMenu__promotionsItem"
                   :href="promo.url"
                   :aria-label="promo.title"
                   @click="actionGA(filteredActiveSection!.name ?? '', promo.title, +index)"
                >
                    <img :src="promo.image" :alt="promo.title">
                </a>
            </div>
            <div v-if="filteredActiveSection?.sectionButton" class="m-mainMenu__bottom">
                <a class="m-mainMenu__bottomLink"
                   :href="filteredActiveSection?.sectionButton.url"
                   @click="handleGACategoryClick('seeAll', filteredActiveSection!.name ?? '')">{{
                        filteredActiveSection?.sectionButton.title
                    }}</a>
            </div>
            <div v-else-if="isActiveSection(MainMenuSection.Profile)" class="m-mainMenu__bottom">
                <a v-if="loggedIn" class="m-mainMenu__bottomLink"
                   @click=handleLogout()>
                    <span v-translate="{ key: Translation.Logout }">Logout</span>
                    <span class="icon">
                        <IconComponent :icon-id="IconName.Logout"/>
                    </span>
                </a>
                <a v-else class="m-mainMenu__bottomLink"
                   :href="data.profile.loginUrl"
                   :aria-label="data.profile.title"
                   @click="handleGACategoryClick('Login', MainMenuSection.Profile)">
                    <span v-translate="{ key: Translation.Login }">Login</span>
                    <span class="icon">
                        <IconComponent :icon-id="IconName.Login"/>
                    </span>
                </a>
            </div>
        </template>

        <template v-else>
            <ul class="m-mainMenu__list">
                <li v-for="(item, idx) of data.menu" :key="idx" class="m-mainMenu__listItem">
                    <a v-if="!isMobile" class="m-mainMenu__listItemTitle"
                       :href="item.url"
                       :aria-label="item.title"
                       @click="toggleContent(item.name, $event)"
                    >{{ item.title }}</a>
                    <a v-else class="m-mainMenu__listItemTitle is-mobile"
                       :href="item.url"
                       :aria-label="item.title"
                       @click="toggleContent(item.name, $event)"
                    >
                        <div class="m-mainMenu__listItemRow">
                            <span>{{ item.title }}</span>
                            <span v-if="item.categories && item.categories.length" class="a-iconArr icon-rarr">
                                <IconComponent :icon-id="IconName.ArrowSlim"/>
                            </span>
                        </div>
                    </a>
                </li>
            </ul>
        </template>
    </div>
</template>
